// src/utils/dbOperationsComb/dbFunctions.js
//
// Every Postgres function (RPC) the app calls, one named function each. Kept
// apart from DBOperationsComb's generic table actions (insert / update /
// delete / fetch), but under dbOperationsComb because everything callable
// goes through comb.
//
// Each takes readable arguments, builds the `p_*` params itself — this file is
// the only place that knows Postgres parameter names — and calls
// DBOperationsComb.useSupabaseRPC with the name from
// Constants.enums.DATABASE_FUNCTION_ENUM. RPCs are online-only: offline, comb
// returns an error without calling Supabase.
//
// Every function returns `{ data, error }` as comb gives it.

import DBOperationsComb from "./dbOperations";
import Constants from "src/utils/constants";

// The form / PDF builders classify failures by Postgres error code (version
// conflict, lock timeout, not found), so their calls keep the raw error.
const RAW_ERROR = { rawError: true };

// ---------------------------------------------------------------------------
// Form builder — draft / publish (formBuilder/transactionalSave.js)
// ---------------------------------------------------------------------------

async function saveFormTemplateDraft({
  templateId,
  expectedDraftVersion,
  draftPayload,
  userId
}) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.SAVE_FORM_TEMPLATE_DRAFT,
    {
      p_template_id: templateId,
      p_expected_draft_version: expectedDraftVersion,
      p_draft_payload: draftPayload,
      p_user_id: userId
    },
    RAW_ERROR
  );
}

async function publishFormTemplate({
  templateId,
  expectedVersion,
  userId,
  draftedEmails = []
}) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.PUBLISH_FORM_TEMPLATE,
    {
      p_template_id: templateId,
      p_expected_version: expectedVersion,
      p_user_id: userId,
      p_drafted_emails: draftedEmails
    },
    RAW_ERROR
  );
}

async function discardFormTemplateDraft({
  templateId,
  expectedDraftVersion,
  userId
}) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.DISCARD_FORM_TEMPLATE_DRAFT,
    {
      p_template_id: templateId,
      p_expected_draft_version: expectedDraftVersion,
      p_user_id: userId
    },
    RAW_ERROR
  );
}

// ---------------------------------------------------------------------------
// PDF builder — draft / publish (pdfBuilder/transactionalSave.js)
// ---------------------------------------------------------------------------

async function savePdfLayoutDraft({
  layoutId,
  expectedDraftVersion,
  draftPayload,
  userId
}) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.SAVE_PDF_LAYOUT_DRAFT,
    {
      p_layout_id: layoutId,
      p_expected_draft_version: expectedDraftVersion,
      p_draft_payload: draftPayload,
      p_user_id: userId
    },
    RAW_ERROR
  );
}

async function publishPdfLayout({ layoutId, expectedVersion, userId }) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.PUBLISH_PDF_LAYOUT,
    {
      p_layout_id: layoutId,
      p_expected_version: expectedVersion,
      p_user_id: userId
    },
    RAW_ERROR
  );
}

async function discardPdfLayoutDraft({
  layoutId,
  expectedDraftVersion,
  userId
}) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.DISCARD_PDF_LAYOUT_DRAFT,
    {
      p_layout_id: layoutId,
      p_expected_draft_version: expectedDraftVersion,
      p_user_id: userId
    },
    RAW_ERROR
  );
}

// ---------------------------------------------------------------------------
// Blades (api/blades)
// ---------------------------------------------------------------------------

async function getBladesUnitsWithChangeInfo({ shopIds }) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.GET_BLADES_UNITS_WITH_CHANGE_INFO,
    { p_shop_ids: shopIds }
  );
}

async function getBladeEventDetails({ eventId }) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.GET_BLADE_EVENT_DETAILS,
    { p_event_id: eventId }
  );
}

async function getBladesInventoryReconciliationDiscrepancies() {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM
      .BLADES_INVENTORY_RECONCILIATION_DISCREPANCIES
  );
}

// ---------------------------------------------------------------------------
// Email templates (api/emailTemplates) — callers normalise values first
// ---------------------------------------------------------------------------

async function createEmailTemplateWithDistributionGroup({
  name,
  template,
  description,
  source,
  userId,
  defaultSendingData,
  sendConfigs
}) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM
      .CREATE_EMAIL_TEMPLATE_WITH_DISTRIBUTION_GROUP,
    {
      p_name: name,
      p_template: template,
      p_description: description,
      p_source: source,
      p_user_id: userId,
      p_default_sending_data: defaultSendingData,
      p_send_configs: sendConfigs
    }
  );
}

async function updateEmailTemplateWithDistributionGroup({
  id,
  name,
  template,
  description,
  source,
  userId,
  defaultSendingData,
  sendConfigs
}) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM
      .UPDATE_EMAIL_TEMPLATE_WITH_DISTRIBUTION_GROUP,
    {
      p_template_id: id,
      p_name: name,
      p_template: template,
      p_description: description,
      p_source: source,
      p_user_id: userId,
      p_default_sending_data: defaultSendingData,
      p_send_configs: sendConfigs
    }
  );
}

async function deleteEmailTemplateWithDistributionGroup({ id, userId }) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM
      .DELETE_EMAIL_TEMPLATE_WITH_DISTRIBUTION_GROUP,
    {
      p_template_id: id,
      p_user_id: userId
    }
  );
}

// ---------------------------------------------------------------------------
// Schema browser (api/schemaBrowser) — admin-gated server-side
// ---------------------------------------------------------------------------

async function listPublicTables() {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.LIST_PUBLIC_TABLES,
    {}
  );
}

async function listPublicTableColumns({ tableName }) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.LIST_PUBLIC_TABLE_COLUMNS,
    { p_table_name: tableName }
  );
}

// ---------------------------------------------------------------------------
// Dynamic reports (api/dynamicReport)
// ---------------------------------------------------------------------------

// The one RPC whose name is not in DATABASE_FUNCTION_ENUM: it comes from the
// selected report form's stored config, and so do the params.
async function runDynamicReport(functionName, params) {
  return await DBOperationsComb.useSupabaseRPC(functionName, params);
}

// ---------------------------------------------------------------------------
// Dynamic forms (api/dynamicForms) — create a form's submissions table
// ---------------------------------------------------------------------------

// Creates the table, or adds any missing columns.
async function checkAndCreateTable({ tableName, columnNames }) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.CHECK_AND_CREATE_TABLE,
    {
      p_table_name: tableName,
      p_column_names: columnNames
    }
  );
}

async function setupRlsForAuthenticatedUsers({ tableName }) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.SETUP_RLS_FOR_AUTHENTICATED_USERS,
    { p_table_name: tableName }
  );
}

// ---------------------------------------------------------------------------
// Patrol report (api/patrolReport) — main client only; the GPS tracker
// client's RPCs are handled separately
// ---------------------------------------------------------------------------

// `priority` is matched as a regex by the function ("1", or a pattern for
// several priorities).
async function getDefectsReport({
  startDate,
  endDate = null,
  defectTypeIds = null,
  statuses,
  priority,
  areaIds,
  regionId
}) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.GET_WEEKLY_DEFECTS_REPORT,
    {
      p_start_date: startDate,
      p_end_date: endDate,
      p_defect_type_ids: defectTypeIds,
      p_statuses: statuses,
      p_priority: priority,
      p_area_ids: areaIds,
      p_region_id: regionId
    }
  );
}

// ---------------------------------------------------------------------------
// LWI dashboard (api/lwiDash)
// ---------------------------------------------------------------------------

async function getStatusCounts({
  formTemplateId,
  areaIds,
  dateRange,
  regionId
}) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.GET_STATUS_COUNTS,
    {
      p_form_template_id: formTemplateId,
      p_area_ids: areaIds,
      p_date_range: dateRange,
      p_region_id: regionId
    }
  );
}

async function getInspectionCounts({
  formTemplateId,
  areaIds,
  dateRange,
  regionId
}) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.GET_INSPECTION_COUNTS,
    {
      p_form_template_id: formTemplateId,
      p_area_ids: areaIds,
      p_date_range: dateRange,
      p_region_id: regionId
    }
  );
}

// `p_statuses` is only sent when statuses are given.
async function getEmergencyResponseCounts({
  formTemplateId,
  areaIds,
  dateRange,
  regionId,
  statuses = null
}) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.GET_EMERGENCY_RESPONSE_COUNTS,
    {
      p_form_template_id: formTemplateId,
      p_area_ids: areaIds,
      p_date_range: dateRange,
      p_region_id: regionId,
      ...(statuses ? { p_statuses: statuses } : {})
    }
  );
}

async function getFlhaSummary({
  formTemplateId,
  areaIds,
  regionId,
  yearRange,
  thisMonth,
  lastMonth,
  thisWeek,
  lastWeek
}) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.GET_FLHA_SUMMARY,
    {
      p_form_template_id: formTemplateId,
      p_area_ids: areaIds,
      p_region_id: regionId,
      p_year_range: yearRange,
      p_this_month: thisMonth,
      p_last_month: lastMonth,
      p_this_week: thisWeek,
      p_last_week: lastWeek
    }
  );
}

async function getHighwayPins({
  formTemplateId,
  typeId,
  areaIds,
  highwayIds,
  searchTxt,
  statuses,
  dateRange,
  regionId,
  priority,
  number
}) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.GET_HIGHWAY_PINS,
    {
      p_form_template_id: formTemplateId,
      p_defect_type_id: typeId,
      p_defect_area_ids: areaIds,
      p_defect_highway_ids: highwayIds,
      p_defect_search_string: searchTxt,
      p_defect_statuses: statuses,
      p_date_range: dateRange,
      p_region_id: regionId,
      p_priority: priority,
      p_defect_number: number
    }
  );
}

async function getInspectionHighwayPins({
  formTemplateId,
  typeId,
  areaIds,
  highwayIds,
  searchTxt,
  dateRange,
  regionId,
  number
}) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.GET_INSPECTION_HIGHWAY_PINS,
    {
      p_form_template_id: formTemplateId,
      p_inspection_type_id: typeId,
      p_inspection_area_ids: areaIds,
      p_inspection_highway_ids: highwayIds,
      p_inspection_search_string: searchTxt,
      p_date_range: dateRange,
      p_region_id: regionId,
      p_inspection_number: number
    }
  );
}

async function getEmergencyResponsePins({
  formTemplateId,
  typeId,
  areaIds,
  highwayIds,
  searchTxt,
  statuses,
  dateRange,
  regionId
}) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.GET_EMERGENCY_RESPONSE_PINS,
    {
      p_form_template_id: formTemplateId,
      p_emergency_type_id: typeId,
      p_area_ids: areaIds,
      p_highway_ids: highwayIds,
      p_search_string: searchTxt,
      p_statuses: statuses,
      p_date_range: dateRange,
      p_region_id: regionId
    }
  );
}

async function getWeeklyEmergencyResponsesExport({
  formTemplateId,
  areaIds,
  regionId,
  dateRange
}) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM
      .GET_WEEKLY_EMERGENCY_RESPONSES_EXPORT,
    {
      p_form_template_id: formTemplateId,
      p_area_ids: areaIds,
      p_region_id: regionId,
      p_date_range: dateRange
    }
  );
}

// ---------------------------------------------------------------------------
// Sequences (api/sequence)
// ---------------------------------------------------------------------------

// Creates a Postgres sequence plus its sequences_log entry; `data` is the new
// sequence's name.
async function createSequence({ readableName, description = null }) {
  return await DBOperationsComb.useSupabaseRPC(
    Constants.enums.DATABASE_FUNCTION_ENUM.CREATE_SEQUENCE,
    {
      readable_name: readableName,
      description
    }
  );
}

export default {
  saveFormTemplateDraft,
  publishFormTemplate,
  discardFormTemplateDraft,
  savePdfLayoutDraft,
  publishPdfLayout,
  discardPdfLayoutDraft,
  getBladesUnitsWithChangeInfo,
  getBladeEventDetails,
  getBladesInventoryReconciliationDiscrepancies,
  createEmailTemplateWithDistributionGroup,
  updateEmailTemplateWithDistributionGroup,
  deleteEmailTemplateWithDistributionGroup,
  listPublicTables,
  listPublicTableColumns,
  runDynamicReport,
  checkAndCreateTable,
  setupRlsForAuthenticatedUsers,
  getDefectsReport,
  getStatusCounts,
  getInspectionCounts,
  getEmergencyResponseCounts,
  getFlhaSummary,
  getHighwayPins,
  getInspectionHighwayPins,
  getEmergencyResponsePins,
  getWeeklyEmergencyResponsesExport,
  createSequence
};
